/**
 * The UM app's own chrome — and the reason it is a shell of its own.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE THE UM APP LIVES, AND WHY
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * **Inside `apps/web`, on the same origin and behind the same sign-in as the Control
 * Centre.** The specs say so in four places and the workspace was created for it:
 *
 *  * `um-app/00-overview.md` §7.2 — "React/Vite front-end compiled to static files …
 *    Everything served from the apex `hockeytd.org` — statics plus `/api` on one hostname,
 *    which keeps magic links short."
 *  * §10 — "The Report Studio (06) is the laptop layout of **the same application and the
 *    same login** — 'the phone captures, the laptop writes'."
 *  * `um-app/09-auth-onboarding.md` §3.1 — the sign-in landing is `/` on this origin, and
 *    §3.3's redeem "routes to the walkthrough … or to `Today`", i.e. one router.
 *  * This workspace's own `package.json` describes itself as "the TD Control Centre **and
 *    the UM Feedback App PWA**, one origin, one sign-in", and `app/App.tsx` was written
 *    around the same stance ("this application is both the Control Centre and the UM app
 *    PWA on one origin (stance D2)").
 *
 * So this is **not** a second workspace. A separate Vite app would need its own build, its
 * own copy of the session and credential modules, and its own origin or a second static
 * root — and the one thing the specs are unambiguous about is that there is exactly one
 * origin and one credential.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IS NOT INSIDE `Shell`
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `Shell` is the Control Centre masthead: emblem, module strip, TD chip, laptop
 * proportions. The UM app is a phone screen with a three-item bottom bar (spec 00 §10) and
 * a `You` / `Umpires Manager` identity chip (spec 01 §2.1). One shell cannot be both
 * without becoming a conditional, so the router carries two trees under one session gate.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE UM-ONLY GATE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A `td` credential is turned away here, in the client, for the same reason the API's
 * `requireUm` turns it away on the wire: the TD authors his own technical-officials stream
 * in a separate store (spec 00 §2), and a capture screen that let him type into this one
 * would be offering to mix them. The client gate is a courtesy that stops him reaching a
 * screen whose every write would 403; the API's gate is the one that enforces it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SYNC CHIP, AND WHERE THE QUEUE IS STARTED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The chips of spec 10 §3 now render here, in the chrome, rather than only on Today. §3 asks
 * for them "on the Today screen (`.pillrow`) and in the desktop chrome", and one placement in
 * the chrome satisfies both while adding something neither asks for and both want: the state
 * of the queue is visible from the capture screen, which is the screen a UM is actually
 * looking at while the queue is filling.
 *
 * **The queue is started here too**, for the whole UM session, and not in a page: a note
 * captured on the capture screen must keep draining while its author walks to the next pitch
 * and lands on Today. It is started after the UM gate below, because the Control Centre has
 * no note writes and a TD is turned away before this point.
 *
 * Still absent rather than stubbed: the daily-report pill and the Settings sheet.
 *
 * **The pill stays absent even now that the Debrief tab is live (2026-09-08).** It is spec 01
 * §2.7's badge, and a badge means "unread since it was generated" — which needs the per-UM
 * `viewed` event, which this build does not have. The tab itself is
 * the way in; a pill that could never badge would be a second, quieter route to the same
 * screen wearing a promise it cannot keep.
 */

import { useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { startNoteQueue } from "../api/client.js";
import { SyncChip } from "../features/um/SyncChip.js";
import { RefreshBanner } from "./RefreshBanner.js";
import { useSession, useTournament } from "./session.js";

export const UM_ROOT_PATH = "/um";

/**
 * Spec 00 §10 and 01 §2.10: bottom-anchored, the only persistent navigation.
 *
 * **FOUR items as of 2026-09-09, where both specs say three.** The fourth is `My Notes`, and it
 * is the Technical Delegate's own product decision from live UM-side testing that day: *"there is
 * nowhere on the app UI for me as a UM to go back to view the feedbacks I have added."* He is
 * right — until this run a note could be read only inside the capture screen of its own match,
 * so the app had no answer to *"what have I written?"* at all. The specs' three-item bar was
 * drawn before the capture surface existed to be reviewed; a decision by the TD supersedes a
 * count in a mockup, and it is recorded here rather than left to be rediscovered as a deviation.
 *
 * It sits SECOND, between `Today` and `Umpires`: `Today` is where a note is written and `My
 * Notes` is where it is read back, and the two belong beside each other. The bar's grid is no
 * longer fixed at three columns (`editorial.css`, run 63) so a fifth item needs no CSS either.
 */
const UM_TABS: ReadonlyArray<{ label: string; path: string; end: boolean }> = [
  { label: "Today", path: UM_ROOT_PATH, end: true },
  // Every note this manager has written, tournament-wide (`UmMyNotesPage`). A READ: every press
  // that changes a note stays on the capture screen, which owns the release semantics of spec 03.
  { label: "My Notes", path: `${UM_ROOT_PATH}/notes`, end: false },
  // `Umpires` lands on the manager's own umpires — the §B3b marking and report surface.
  // Spec 04's evidence timeline is not built yet and joins those rows when it is.
  //
  // ~~`Debrief` (spec 05) is not built. It is shown, disabled and labelled, rather than
  // hidden: a bar that silently lost an item would leave a UM wondering what they had done
  // wrong.~~ **SUPERSEDED 2026-09-08.** The Debrief Pack is built and this tab is live. It is
  // the deterministic slice of spec 05 rather than the whole screen — the pack is computed
  // when the tab is opened rather than generated to a schedule, and the AI one-liners, the two
  // flag types that need sentiment, the `Discussed` mark and the A4 document are absent
  // rather than stubbed. `UmDebriefPage`'s own doc-comment names each one and why.
  //
  // **The A4 document was built in run 66** and lives UNDER this tab, at `debrief/print` —
  // which is why the tab's `end` is `false`: `Debrief` stays lit while a manager is looking at
  // the paper, rather than the bar going dark on a screen that is plainly still the debrief.
  { label: "Umpires", path: `${UM_ROOT_PATH}/umpires`, end: false },
  // **RENAMED `Debrief` → `Daily Report` (TD ruling, 2026-09-11, run 73c).** The word
  // `Debrief` now belongs to the per-match Match Debrief alone; the nightly artefact is a
  // Report. The route is unchanged — links in the wild keep working.
  { label: "Daily Report", path: `${UM_ROOT_PATH}/debrief`, end: false },
];

/**
 * Every tab is built as of 2026-09-08, so this set is now the whole of `UM_TABS`.
 *
 * It is KEPT rather than deleted along with the last disabled item: the mechanism is what
 * keeps a half-built tab honest, and the next screen to be specified before it is written
 * (spec 04's timeline is the obvious candidate) needs it back on the day it is added. An
 * empty exception list is a cheaper thing to carry than a rule somebody has to rediscover.
 */
const BUILT_TABS = new Set([
  UM_ROOT_PATH,
  `${UM_ROOT_PATH}/notes`,
  `${UM_ROOT_PATH}/umpires`,
  `${UM_ROOT_PATH}/debrief`,
]);

export function UmShell(): React.JSX.Element {
  const { session } = useSession();
  const tournament = useTournament();
  const identity = session.status === "signed-in" ? session.identity : null;
  const isUm = identity === null || identity.kind === "um";

  // Load the queue from the phone and keep it draining — on `online`, on focus, and on the
  // 30-second foreground timer of spec 10 §2.2. Nothing is pressed to make this happen, and
  // its teardown is returned so a re-render cannot leave two timers running.
  useEffect(() => {
    if (!isUm) return undefined;
    return startNoteQueue();
  }, [isUm]);

  if (identity !== null && identity.kind !== "um") {
    return (
      <div className="umapp">
        <RefreshBanner />
        <section className="panel panel--d1">
          <div className="panel__head">
            <h3 className="panel__title">This Is The Umpires Managers&rsquo; App</h3>
            <span className="panel__note">Signed In</span>
          </div>
          <div className="panel__foot">
            Capture and release belong to the six Umpires Managers. Your own notes are a
            separate stream and are not written here. The Control Centre is at the root of
            this site.
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="umapp">
      {/* The phone that keeps this app open for days is the whole reason the bar exists — the
          TD's live-testing finding of 2026-09-09. It is above the chrome because it is about
          the page rather than about a screen, and it renders nothing until a newer release is
          deployed. See `RefreshBanner.tsx`. */}
      <RefreshBanner />
      <header className="umapp__head">
        <div>
          <h1 className="umapp__title">Umpire Feedback Companion</h1>
          {/* The event, from the tournament record and not from a constant — the same fix as
              the Control Centre masthead, for the same reason. The kicker is dropped entirely
              while the tournament is unknown rather than showing a placeholder: the title
              above it already says what the application is. */}
          {tournament === null ? null : (
            <span className="umapp__kicker" data-testid="um-event-kicker">
              {tournament.shortDisplayName}
            </span>
          )}
          {/* Spec 10 §3. It is in the chrome so it is on every screen, including the one a
              UM is looking at while the queue fills. */}
          <SyncChip />
        </div>
        {/* Spec 01 §2.1: the avatar reads `UM` for everybody and the name line is always
            the literal `You` — never the UM's own name. */}
        <div className="umchip">
          <span className="umchip__avatar" aria-hidden="true">
            UM
          </span>
          <span>
            <span className="umchip__name">You</span>
            <br />
            <span className="umchip__role">Umpires Manager</span>
          </span>
        </div>
      </header>

      <main className="umapp__body">
        <Outlet />
      </main>

      <nav className="umtabs" aria-label="Sections">
        {UM_TABS.map((tab) =>
          BUILT_TABS.has(tab.path) ? (
            <NavLink key={tab.label} to={tab.path} end={tab.end}>
              {tab.label}
            </NavLink>
          ) : (
            <span key={tab.label} className="umtabs__soon" aria-disabled="true">
              {tab.label}
            </span>
          ),
        )}
      </nav>
    </div>
  );
}
